import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Minus, User, Headphones } from 'lucide-react';
import { useChat } from '../contexts/ChatContext';
import { useAuth } from '../contexts/AuthContextMySQL';

export default function ChatWidget() {
  const { 
    isOpen, 
    toggleChat, 
    closeChat, 
    activeConversation, 
    sendMessage, 
    unreadCount,
    createConversation 
  } = useChat();
  const { isAuthenticated } = useAuth();
  const [message, setMessage] = useState('');
  const chatRef = useRef(null);

  const scrollToBottom = () => {
    // Défile uniquement le conteneur du widget, jamais la page
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [activeConversation?.messages]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    
    if (!isAuthenticated) {
      // Show login prompt
      return;
    }
    
    sendMessage(message);
    setMessage('');
  };

  const handleOpenChat = () => {
    if (!isAuthenticated) {
      // Still allow opening but show auth message
      toggleChat();
    } else {
      toggleChat();
    }
  };

  return (
    <>
      {/* Floating Button */}
      <motion.button
        onClick={handleOpenChat}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center shadow-[0_0_20px_rgba(250,204,21,0.4)]"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 2 }}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-black" />
        ) : (
          <>
            <MessageCircle className="w-6 h-6 text-black" />
            {unreadCount > 0 && (
              <motion.span
                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring" }}
              >
                {unreadCount}
              </motion.span>
            )}
          </>
        )}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed bottom-24 right-6 z-50 w-96 bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl overflow-hidden"
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ type: "spring", damping: 25 }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-black/20 rounded-full flex items-center justify-center">
                  <Headphones className="w-5 h-5 text-black" />
                </div>
                <div>
                  <h3 className="font-bold text-black">Support AV Kamas</h3>
                  <p className="text-xs text-black/70 flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full" />
                    En ligne
                  </p>
                </div>
              </div>
              <div className="flex gap-1">
                <button 
                  onClick={closeChat}
                  className="p-2 hover:bg-black/20 rounded-full transition-colors"
                >
                  <Minus className="w-4 h-4 text-black" />
                </button>
                <button 
                  onClick={closeChat}
                  className="p-2 hover:bg-black/20 rounded-full transition-colors"
                >
                  <X className="w-4 h-4 text-black" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div ref={chatRef} className="h-80 overflow-y-auto p-4 space-y-3 bg-gray-950">
              {!isAuthenticated ? (
                <div className="text-center py-8">
                  <User className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400 mb-4">Connectez-vous pour discuter avec notre support</p>
                  <div className="flex gap-2 justify-center">
                    <a 
                      href="/login" 
                      className="px-4 py-2 bg-yellow-500 text-black rounded-lg text-sm font-medium hover:bg-yellow-400"
                    >
                      Se connecter
                    </a>
                    <a 
                      href="/register" 
                      className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-700"
                    >
                      S'inscrire
                    </a>
                  </div>
                </div>
              ) : !activeConversation ? (
                <div className="text-center py-8">
                  <MessageCircle className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400 mb-4">Démarrez une conversation avec notre équipe</p>
                  <button
                    onClick={createConversation}
                    className="px-4 py-2 bg-yellow-500 text-black rounded-lg text-sm font-medium hover:bg-yellow-400"
                  >
                    Nouvelle conversation
                  </button>
                </div>
              ) : (
                <>
                  {activeConversation.messages?.map((msg) => (
                    <motion.div
                      key={msg.id}
                      className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <div
                        className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                          msg.sender === 'user'
                            ? 'bg-yellow-500 text-black rounded-br-none'
                            : 'bg-gray-800 text-white rounded-bl-none'
                        }`}
                      >
                        <p className="text-sm">{msg.text}</p>
                        <span className="text-xs opacity-60 mt-1 block">
                          {new Date(msg.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </>
              )}
            </div>

            {/* Input */}
            {isAuthenticated && activeConversation && (
              <form onSubmit={handleSend} className="p-3 bg-gray-900 border-t border-gray-800">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Écrivez votre message..."
                    className="flex-1 bg-gray-950 border border-gray-700 rounded-full px-4 py-2 text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none"
                  />
                  <motion.button
                    type="submit"
                    disabled={!message.trim()}
                    className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center disabled:opacity-50"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Send className="w-5 h-5 text-black" />
                  </motion.button>
                </div>
              </form>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
